import { Button } from "@/components/ui/baseUi/button"
import { Input } from "@/components/ui/baseUi/input"
import { Text } from "@/components/ui/baseUi/text"
import { withForm } from "@/components/ui/forms/hooks/useFormHook"
import { useBoxPrograms } from "@/modules/boxProgrammation/hooks/useBoxProgram"
import { HUMultiSelect } from "@/modules/ui/huMultiSelect"
import { LockOutlinedIcon } from "@images/svgIcons/lockOutlinedIcon"
import { type BoxOfferFormPageProps } from "@modules/boxOffer/components/boxOfferForm"
import { BoxOfferFormRulesInput } from "@modules/boxOffer/components/boxOfferFormRulesInput"
import { BoxOfferFormSection, BoxOfferInputCustomLabel } from "@modules/boxOffer/components/boxOfferFormSection"
import { BoxOfferType } from "@modules/boxOffer/entities/boxOfferTypes"
import { boxOfferFormDefaultValues } from "@modules/boxOffer/types/boxOfferFormTypes"
import { word } from "@modules/core/utils/i18n"
import { useStore } from "@tanstack/react-form"
import { ArrowLeftIcon, ArrowRightIcon } from "lucide-react"
import { type SyntheticEvent, useState } from "react"

export const BoxOfferFormSecondPage = withForm({
  defaultValues: boxOfferFormDefaultValues,
  props: {} as BoxOfferFormPageProps,
  render: function BoxOfferFormSecondPageInner({ form, handleSubmit, setPageNumber, isEdition }) {
    const [disabled, setDisabled] = useState(false)
    const { data: programs } = useBoxPrograms()

    const type = useStore(form.baseStore, (state) => state.values.type)

    return (
      <div className="flex flex-col gap-8 px-5 pt-20 pb-4">
        <div className="flex flex-col gap-2">
          <Text fontStyle="TL">{`${isEdition ? word(`offer.form.${type}.edit`) : word(`offer.form.${type}.create`)} (2/3)`}</Text>
          <Text fontStyle="BM" className="text-neutral-700">
            {word(`offer.form.${type}.subtitle`)}
          </Text>
        </div>
        <BoxOfferFormSection
          title={word("box.offer.form.infos.activitiesAccess.title")}
          icon={<LockOutlinedIcon color="currentColor" />}
          description={
            [BoxOfferType.Subscription, BoxOfferType.Trial, BoxOfferType.TicketsBook, BoxOfferType.Millenium].includes(
              type,
            )
              ? word("box.offer.form.infos.activitiesAccess.details")
              : undefined
          }
          className={
            [BoxOfferType.Subscription, BoxOfferType.Trial, BoxOfferType.TicketsBook, BoxOfferType.Millenium].includes(
              type,
            )
              ? "mb-5"
              : undefined
          }
        />
        {/* Number of sessions */}
        {type === BoxOfferType.Trial && (
          <form.AppField name="count">
            {(field) => (
              <field.Field>
                <field.Label required htmlFor={field.name}>
                  {word("offer.form.numberOfSessions.title")}
                </field.Label>
                <field.Content>
                  <field.Description>{word("offer.form.numberOfSessions.TRIAL.description")}</field.Description>
                  <Input
                    id={field.name}
                    name={field.name}
                    className="w-full"
                    type="number"
                    inputMode="numeric"
                    step={1}
                    placeholder={word("offer.form.numberOfSessions.title")}
                    value={field.state.value ?? ""}
                    onBlur={field.handleBlur}
                    onChange={(e) => {
                      const parsed = parseInt(e.target.value)
                      field.handleChange(isNaN(parsed) ? undefined : parsed)
                    }}
                    onBeforeInput={(e: SyntheticEvent<HTMLInputElement>) => {
                      if ("data" in e && isNaN(Number(e.data))) {
                        e.preventDefault()
                      }
                    }}
                    aria-invalid={field.state.meta.isTouched && !field.state.meta.isValid}
                  />
                  <field.Message />
                </field.Content>
              </field.Field>
            )}
          </form.AppField>
        )}
        {/* Rules */}
        {type !== BoxOfferType.DropIn && (
          <form.AppField name="rules">
            {(field) => (
              <BoxOfferFormRulesInput
                rules={field.state.value ?? []}
                onChangeRules={(updatedRules) => {
                  field.handleChange(updatedRules)
                }}
                setDisabled={setDisabled}
              />
            )}
          </form.AppField>
        )}
        {/* Renewable trial */}
        {type === BoxOfferType.Trial && (
          <div className="flex flex-col gap-2">
            <BoxOfferInputCustomLabel
              label={word("box.offer.form.infos.renewal.title")}
              description={word("box.offer.form.infos.renewal.subtitle.TRIAL")}
              isRequired
              margin="mb-5"
            />

            <form.AppField name="renewable">
              {(field) => (
                <field.Field>
                  <field.Content>
                    <field.SwitchInline textSwitch={word("box.offer.form.infos.renewal.description")} />
                    <field.Message />
                  </field.Content>
                </field.Field>
              )}
            </form.AppField>
          </div>
        )}

        {/* Activities included */}
        {programs.length > 0 && (
          <form.AppField name="programs">
            {(field) => {
              const onToggleAllPrograms = () => {
                if (field.state.value.length === programs.length) {
                  field.handleChange([])
                } else {
                  field.handleChange(programs.map((program) => program.id))
                }
              }

              return (
                <div className="my-5">
                  <BoxOfferInputCustomLabel
                    label={word("box.offer.form.infos.activitiesIncluded.title")}
                    description={word("box.offer.form.infos.activitiesIncluded.details")}
                    isRequired
                  />
                  <div className="flex flex-col gap-5 rounded-2xl border-2 border-solid border-neutral-200 bg-neutral-50 px-5 py-4">
                    <div className="flex w-full flex-row items-start justify-between gap-5">
                      <Text fontStyle="TS">{word("box.offer.form.infos.myActivities.title")}</Text>
                      <div className={"flex flex-col items-end"}>
                        <Text fontStyle="BS">{`${field.state.value.length.toString()}/${programs.length.toString()} ${word(
                          "global.selected",
                          {
                            count: field.state.value.length === 0 ? 1 : field.state.value.length,
                          },
                        )}`}</Text>

                        <Button variant="underline" size="xs" onClick={onToggleAllPrograms} className="pr-0">
                          <Text fontStyle="LS">
                            {field.state.value.length !== programs.length
                              ? word("global.filters.selectAll")
                              : word("global.filters.unselectAll")}
                          </Text>
                        </Button>
                      </div>
                    </div>

                    <HUMultiSelect
                      options={programs.map((program) => ({
                        label: program.name,
                        value: program.id,
                      }))}
                      values={field.state.value}
                      viewMore={word("box.offer.form.infos.myActivities.showMore")}
                      onChange={(value) => {
                        field.handleChange(value)
                      }}
                    />

                    <field.Message className="mt-2" />
                  </div>
                </div>
              )
            }}
          </form.AppField>
        )}

        <div className="mt-5 mb-4 flex gap-5">
          <Button
            variant="outline"
            size="lg"
            onClick={() => {
              setPageNumber((prev) => prev - 1)
            }}
            className="grow px-3 py-2"
          >
            <ArrowLeftIcon color="currentColor" />
            {word("global.previous")}
          </Button>

          <Button variant="primary" size="lg" onClick={handleSubmit} className="grow px-3 py-2" disabled={disabled}>
            {word("global.next")}
            <ArrowRightIcon color="currentColor" />
          </Button>
        </div>
      </div>
    )
  },
})
